import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, boolean, integer, decimal } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { omitSchemaFields } from "./schema-utils";
import { z } from "zod";

// News Articles Schema
export const articles = pgTable("articles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // 로컬 봇 통합 필드
  sourceHash: varchar("source_hash", { length: 64 }).unique(), // 중복 방지용 SHA-256 해시
  createdBy: varchar("created_by", { length: 50 }).default("editor"), // "bot" or "editor"
  importMetadata: text("import_metadata"), // JSON: {bot_version, generation_time_ms, ai_model, scraped_from, original_url}
  
  // 기본 정보
  title: text("title").notNull(),
  titleKo: text("title_ko"),
  content: text("content").notNull(),
  contentKo: text("content_ko"),
  summary: text("summary").notNull(),
  summaryKo: text("summary_ko"),
  category: text("category").notNull(),
  tags: text("tags").array().default(sql`'{}'::text[]`),
  region: text("region").default("ALL"), // Australian state: NSW, VIC, QLD, SA, WA, TAS, NT, ACT, ALL
  status: text("status").notNull().default("published"), // draft, pending, approved, published, unpublished
  isBreaking: boolean("is_breaking").default(false),
  
  // 이미지
  imageUrl: text("image_url"),
  imageDescription: text("image_description"),
  imageSource: varchar("image_source", { length: 50 }), // "unsplash", "r2", "pexels"
  keyHighlight: text("key_highlight"),
  
  // 출처
  sourceUrl: text("source_url"),
  author: text("author"),
  
  // 통계
  views: integer("views").default(0),
  
  // 타임스탬프
  publishedAt: timestamp("published_at").notNull().defaultNow(),
  publishedBy: varchar("published_by", { length: 100 }), // 발행자 이메일
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  
  // SEO Fields
  metaDescription: text("meta_description"), // 150-160 chars for search engines
  metaDescriptionKo: text("meta_description_ko"),
  focusKeywords: text("focus_keywords").array().default(sql`'{}'::text[]`), // Main SEO keywords
  slug: text("slug"), // URL-friendly version of title
  
  // Scope-based filtering fields
  scope: text("scope").default("national"), // "global" | "national" | "local"
  countryOrigin: text("country_origin"), // e.g., "AU", "KR", "US" (for international news)
  state: text("state"), // e.g., "NSW", "VIC" (for local news)
  city: text("city"), // e.g., "Sydney", "Melbourne" (for local news)
  suburb: text("suburb"), // e.g., "Epping", "Parramatta" (for local news)
  localTopic: text("local_topic"), // "life" | "biz" | "education" | "culture" | "notice" (for local news)
});

export const insertArticleSchema = omitSchemaFields(createInsertSchema(articles), {
  id: true,
  createdAt: true,
});

export type InsertArticle = z.infer<typeof insertArticleSchema>;
export type Article = typeof articles.$inferSelect;

// General News Categories (9 categories - global/national scope)
export const generalCategories = [
  { id: "international", label: "국제/세계", labelEn: "International", journalist: "루이스 나", journalistEn: "Louis Na", scope: "global" },
  { id: "politics", label: "정치", labelEn: "Politics", journalist: "제프리 한", journalistEn: "Jeffrey Han", scope: "national" },
  { id: "economy", label: "경제/비즈니스", labelEn: "Economy/Business", journalist: "필립 강", journalistEn: "Philip Kang", scope: "national" },
  { id: "tech", label: "기술/IT", labelEn: "Technology/IT", journalist: "하나 리", journalistEn: "Hana Lee", scope: "national" },
  { id: "sports", label: "스포츠", labelEn: "Sports", journalist: "제이 박", journalistEn: "Jay Park", scope: "national" },
  { id: "entertainment", label: "엔터/문화", labelEn: "Entertainment/Culture", journalist: "미나 윤", journalistEn: "Mina Yoon", scope: "national" },
  { id: "health", label: "건강/의료", labelEn: "Health/Medical", journalist: "제니 장", journalistEn: "Jenny Jang", scope: "national" },
  { id: "column", label: "사설/칼럼", labelEn: "Editorial/Column", journalist: "데이비드 김", journalistEn: "David Kim", scope: "national" },
  { id: "indepth", label: "특집/인뎁스", labelEn: "Special/In-depth", journalist: "다니엘 최", journalistEn: "Daniel Choi", scope: "national" },
] as const;

// Local News Topics (for local scope articles)
export const localTopics = [
  { id: "life", label: "생활", labelEn: "Life" },
  { id: "biz", label: "비즈니스", labelEn: "Business" },
  { id: "education", label: "교육", labelEn: "Education" },
  { id: "culture", label: "문화", labelEn: "Culture" },
  { id: "notice", label: "공지", labelEn: "Notice" },
] as const;

// Local Journalists by region
export const localJournalists = [
  // NSW
  { name: "Grace Park", nameKo: "그레이스 박", state: "NSW", city: "Sydney" },
  { name: "Ethan Cho", nameKo: "이단 조", state: "NSW", city: "Newcastle" },
  { name: "Sunny Seo", nameKo: "써니 서", state: "NSW", city: "Wollongong" },
  // VIC
  { name: "Alex Yoo", nameKo: "알렉스 유", state: "VIC", city: "Melbourne" },
  { name: "Irene Kim", nameKo: "아이린 김", state: "VIC", city: "Geelong" },
  { name: "Brian Lee", nameKo: "브라이언 이", state: "VIC", city: "Ballarat" },
  // QLD
  { name: "Daniel Park", nameKo: "다니엘 박", state: "QLD", city: "Brisbane" },
  { name: "Yuna Choi", nameKo: "유나 최", state: "QLD", city: "Gold Coast" },
  { name: "Leo Jung", nameKo: "레오 정", state: "QLD", city: "Cairns" },
  // SA
  { name: "Hannah Moon", nameKo: "한나 문", state: "SA", city: "Adelaide" },
  // WA
  { name: "Chris Kang", nameKo: "크리스 강", state: "WA", city: "Perth" },
  // ACT
  { name: "Olivia Han", nameKo: "올리비아 한", state: "ACT", city: "Canberra" },
  // TAS
  { name: "Kevin Lim", nameKo: "케빈 림", state: "TAS", city: "Hobart" },
  // NT
  { name: "Alice Min", nameKo: "앨리스 민", state: "NT", city: "Darwin" },
] as const;

// All categories (legacy - for backward compatibility)
export const categories = [
  ...generalCategories,
  { id: "local", label: "로컬", labelEn: "Local", journalist: "로컬 기자", journalistEn: "Local Reporter", scope: "local" },
] as const;

export type CategoryId = typeof categories[number]["id"];
export type GeneralCategoryId = typeof generalCategories[number]["id"];
export type LocalTopicId = typeof localTopics[number]["id"];

// Article Status type
export const articleStatuses = [
  { id: "draft", label: "임시저장", labelEn: "Draft", color: "gray" },
  { id: "pending", label: "승인대기", labelEn: "Pending", color: "yellow" },
  { id: "approved", label: "승인완료", labelEn: "Approved", color: "blue" },
  { id: "published", label: "게시됨", labelEn: "Published", color: "green" },
  { id: "unpublished", label: "발행취소", labelEn: "Unpublished", color: "red" },
] as const;

export type ArticleStatus = typeof articleStatuses[number]["id"];

// Regions (Australian States)
export const regions = [
  { id: "ALL", label: "전체 지역", labelEn: "All Regions" },
  { id: "NSW", label: "뉴사우스웨일즈", labelEn: "New South Wales" },
  { id: "VIC", label: "빅토리아", labelEn: "Victoria" },
  { id: "QLD", label: "퀸즈랜드", labelEn: "Queensland" },
  { id: "SA", label: "사우스오스트레일리아", labelEn: "South Australia" },
  { id: "WA", label: "웨스턴오스트레일리아", labelEn: "Western Australia" },
  { id: "TAS", label: "태즈메이니아", labelEn: "Tasmania" },
  { id: "NT", label: "노던준주", labelEn: "Northern Territory" },
  { id: "ACT", label: "캔버라", labelEn: "Australian Capital Territory" },
] as const;

// Admin Users Schema (for Firebase Auth)
export const adminUsers = pgTable("admin_users", {
  id: varchar("id").primaryKey(),
  email: text("email").notNull().unique(),
  displayName: text("display_name"),
  photoUrl: text("photo_url"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertAdminUserSchema = omitSchemaFields(createInsertSchema(adminUsers), {
  createdAt: true,
});

export type InsertAdminUser = z.infer<typeof insertAdminUserSchema>;
export type AdminUser = typeof adminUsers.$inferSelect;

// Import Logs Schema (로컬 봇 업로드 로그)
export const importLogs = pgTable("import_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sourceHash: varchar("source_hash", { length: 64 }).notNull(),
  filename: varchar("filename", { length: 255 }),
  status: varchar("status", { length: 20 }).notNull(), // success, duplicate, error
  errorMessage: text("error_message"),
  botIp: varchar("bot_ip", { length: 45 }),
  responseTimeMs: integer("response_time_ms"),
  articleId: varchar("article_id"), // FK to articles
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertImportLogSchema = omitSchemaFields(createInsertSchema(importLogs), {
  id: true,
  createdAt: true,
});

export type InsertImportLog = z.infer<typeof insertImportLogSchema>;
export type ImportLog = typeof importLogs.$inferSelect;

// Comments Schema
export const comments = pgTable("comments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  articleId: varchar("article_id").notNull(),
  userId: varchar("user_id").notNull(), // Firebase UID
  userEmail: text("user_email").notNull(),
  userDisplayName: text("user_display_name").notNull(),
  userPhotoUrl: text("user_photo_url"),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertCommentSchema = omitSchemaFields(createInsertSchema(comments), {
  id: true,
  createdAt: true,
});

export type InsertComment = z.infer<typeof insertCommentSchema>;
export type Comment = typeof comments.$inferSelect;

// Advertisements Schema
export const advertisements = pgTable("advertisements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  advertiser: text("advertiser").notNull(),
  contact: text("contact").notNull(), // 전화번호
  email: text("email"), // 이메일 주소
  businessAddress: text("business_address"), // 사업장 상세 주소
  businessCity: text("business_city"), // 도시
  businessState: text("business_state"), // 주/구역
  businessPostcode: text("business_postcode"), // 우편번호
  region: text("region").default("ALL"), // Target region: NSW, VIC, QLD, SA, WA, TAS, NT, ACT, ALL
  imageUrl: text("image_url").notNull(),
  mobileImageUrl: text("mobile_image_url"), // Optional mobile-optimized image
  link: text("link").notNull(),
  startDate: text("start_date").notNull(), // YYYY-MM-DD
  endDate: text("end_date").notNull(),     // YYYY-MM-DD
  location: text("location").notNull(),     // AdLocation type
  clicks: text("clicks").notNull().default("0"),
  views: text("views").notNull().default("0"),
  status: text("status").notNull().default("active"), // AdStatus type
  plan: text("plan").notNull(),            // AdPlan type
  price: text("price").notNull().default("0"),
  tags: text("tags").array().default(sql`'{}'::text[]`),
  priority: text("priority").default("5"), // 1-10 scale
  monthlyLimit: text("monthly_limit").default("0"), // 0 = unlimited
  impressionsUsed: text("impressions_used").default("0"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertAdvertisementSchema = omitSchemaFields(createInsertSchema(advertisements), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAdvertisement = z.infer<typeof insertAdvertisementSchema>;
export type Advertisement = typeof advertisements.$inferSelect;

// Scheduled Article Generation Schema
export const scheduledArticles = pgTable("scheduled_articles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  category: text("category").notNull(), // 카테고리
  count: text("count").notNull().default("1"), // 생성할 기사 개수
  scheduledAt: timestamp("scheduled_at").notNull(), // 예약 실행 시간
  status: text("status").notNull().default("pending"), // pending, processing, completed, failed, cancelled
  errorMessage: text("error_message"), // 에러 메시지
  generatedArticleIds: text("generated_article_ids").array().default(sql`'{}'::text[]`), // 생성된 기사 ID 목록
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertScheduledArticleSchema = omitSchemaFields(createInsertSchema(scheduledArticles), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertScheduledArticle = z.infer<typeof insertScheduledArticleSchema>;
export type ScheduledArticle = typeof scheduledArticles.$inferSelect;

// Automation Settings Schema
export const automationSettings = pgTable("automation_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(), // 설정 이름 (예: "daily_scraping")
  enabled: boolean("enabled").notNull().default(true), // 활성화 여부
  schedule: text("schedule").notNull(), // cron 표현식 (예: "0 8 * * *")
  type: text("type").notNull(), // scraping, generation
  config: text("config").notNull(), // JSON 설정 (카테고리, 개수 등)
  lastRunAt: timestamp("last_run_at"), // 마지막 실행 시간
  nextRunAt: timestamp("next_run_at"), // 다음 실행 예정 시간
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertAutomationSettingSchema = omitSchemaFields(createInsertSchema(automationSettings), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAutomationSetting = z.infer<typeof insertAutomationSettingSchema>;
export type AutomationSetting = typeof automationSettings.$inferSelect;

// Automation Logs Schema
export const automationLogs = pgTable("automation_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  settingId: varchar("setting_id"), // 자동화 설정 ID
  settingName: text("setting_name").notNull(), // 설정 이름
  type: text("type").notNull(), // scraping, generation
  status: text("status").notNull().default("pending"), // pending, running, success, failed
  articlesScraped: text("articles_scraped").default("0"), // 크롤링된 기사 수
  articlesGenerated: text("articles_generated").default("0"), // 생성된 기사 수
  categoriesProcessed: text("categories_processed").array().default(sql`'{}'::text[]`), // 처리된 카테고리
  errorMessage: text("error_message"), // 에러 메시지
  executionTime: text("execution_time").default("0"), // 실행 시간 (ms)
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertAutomationLogSchema = omitSchemaFields(createInsertSchema(automationLogs), {
  id: true,
  createdAt: true,
});

export type InsertAutomationLog = z.infer<typeof insertAutomationLogSchema>;
export type AutomationLog = typeof automationLogs.$inferSelect;

// Vocabulary Schema (for English learning feature)
export const vocabulary = pgTable("vocabulary", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // Firebase UID
  word: text("word").notNull(),
  definition: text("definition"),
  pronunciation: text("pronunciation"),
  exampleSentence: text("example_sentence"),
  koreanTranslation: text("korean_translation"),
  synonyms: text("synonyms").array().default(sql`'{}'::text[]`),
  antonyms: text("antonyms").array().default(sql`'{}'::text[]`),
  articleId: varchar("article_id"),
  articleTitle: text("article_title"),
  proficiency: text("proficiency").notNull().default("unknown"), // unknown, unsure, known
  savedAt: timestamp("saved_at").notNull().defaultNow(),
  lastReviewedAt: timestamp("last_reviewed_at").notNull().defaultNow(),
});

export const insertVocabularySchema = omitSchemaFields(createInsertSchema(vocabulary), {
  id: true,
  savedAt: true,
  lastReviewedAt: true,
});

export type InsertVocabulary = z.infer<typeof insertVocabularySchema>;
export type Vocabulary = typeof vocabulary.$inferSelect;

// Bookmarks Schema (찜하기)
export const bookmarks = pgTable("bookmarks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // Firebase UID
  itemType: text("item_type").notNull(), // article, video, product, business
  itemId: varchar("item_id").notNull(), // 찜한 항목의 ID
  itemTitle: text("item_title"), // 항목 제목
  itemImageUrl: text("item_image_url"), // 항목 이미지
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertBookmarkSchema = omitSchemaFields(createInsertSchema(bookmarks), {
  id: true,
  createdAt: true,
});

export type InsertBookmark = z.infer<typeof insertBookmarkSchema>;
export type Bookmark = typeof bookmarks.$inferSelect;

// Article Views Schema (조회수 추적)
export const articleViews = pgTable("article_views", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  articleId: varchar("article_id").notNull(),
  userId: varchar("user_id"), // Firebase UID (optional for non-logged in users)
  sessionId: text("session_id").notNull(), // 중복 방지용 세션 ID
  ipAddress: text("ip_address"), // IP 주소
  userAgent: text("user_agent"), // User Agent
  viewedAt: timestamp("viewed_at").notNull().defaultNow(),
});

export const insertArticleViewSchema = omitSchemaFields(createInsertSchema(articleViews), {
  id: true,
  viewedAt: true,
});

export type InsertArticleView = z.infer<typeof insertArticleViewSchema>;
export type ArticleView = typeof articleViews.$inferSelect;

// Activity Logs Schema (활동 기록)
export const activityLogs = pgTable("activity_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // Firebase UID
  activityType: text("activity_type").notNull(), // view_article, save_word, take_quiz, click_phone, etc.
  itemType: text("item_type"), // article, video, product, business, word
  itemId: varchar("item_id"), // 관련 항목 ID
  itemTitle: text("item_title"), // 항목 제목
  metadata: text("metadata"), // JSON string for additional data
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertActivityLogSchema = omitSchemaFields(createInsertSchema(activityLogs), {
  id: true,
  createdAt: true,
});

export type InsertActivityLog = z.infer<typeof insertActivityLogSchema>;
export type ActivityLog = typeof activityLogs.$inferSelect;

// Activity Type enum
export const activityTypes = [
  { id: "view_article", label: "기사 조회", labelEn: "View Article" },
  { id: "view_video", label: "영상 조회", labelEn: "View Video" },
  { id: "view_product", label: "상품 조회", labelEn: "View Product" },
  { id: "view_business", label: "업체 조회", labelEn: "View Business" },
  { id: "save_word", label: "단어 저장", labelEn: "Save Word" },
  { id: "take_quiz", label: "퀴즈 응시", labelEn: "Take Quiz" },
  { id: "use_flashcard", label: "플래시카드 학습", labelEn: "Use Flashcard" },
  { id: "click_phone", label: "전화번호 클릭", labelEn: "Click Phone" },
  { id: "bookmark_add", label: "찜하기 추가", labelEn: "Add Bookmark" },
  { id: "bookmark_remove", label: "찜하기 제거", labelEn: "Remove Bookmark" },
] as const;

export type ActivityType = typeof activityTypes[number]["id"];

// ==========================================
// 🛍️ MARKETPLACE (핫샵) SCHEMAS
// ==========================================

// Marketplace Categories
export const marketplaceCategories = [
  { id: "food", label: "식품/식당", labelEn: "Food/Restaurant", icon: "🍽️" },
  { id: "beauty", label: "미용/뷰티", labelEn: "Beauty/Salon", icon: "💄" },
  { id: "education", label: "교육/학원", labelEn: "Education/Academy", icon: "📚" },
  { id: "health", label: "의료/건강", labelEn: "Medical/Health", icon: "🏥" },
  { id: "realestate", label: "부동산/렌탈", labelEn: "Real Estate/Rental", icon: "🏠" },
  { id: "legal", label: "법률/회계", labelEn: "Legal/Accounting", icon: "⚖️" },
  { id: "auto", label: "자동차/정비", labelEn: "Auto/Repair", icon: "🚗" },
  { id: "shopping", label: "쇼핑/유통", labelEn: "Shopping/Retail", icon: "🛒" },
  { id: "travel", label: "여행/항공", labelEn: "Travel/Flight", icon: "✈️" },
  { id: "service", label: "기타 서비스", labelEn: "Other Services", icon: "🔧" },
] as const;

export type MarketplaceCategoryId = typeof marketplaceCategories[number]["id"];

// Marketplace Items (상품/서비스)
export const marketplaceItems = pgTable("marketplace_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  businessName: text("business_name").notNull(), // 업체명
  category: text("category").notNull(), // MarketplaceCategoryId
  region: text("region").default("ALL"), // NSW, VIC, QLD, SA, WA, TAS, NT, ACT, ALL
  
  // Product/Service Info
  title: text("title").notNull(), // 상품/서비스명
  titleKo: text("title_ko"), // 한글명
  description: text("description").notNull(), // 설명
  descriptionKo: text("description_ko"), // 한글 설명
  
  // Pricing
  originalPrice: decimal("original_price", { precision: 10, scale: 2 }), // 정가
  salePrice: decimal("sale_price", { precision: 10, scale: 2 }).notNull(), // 판매가
  discountRate: integer("discount_rate"), // 할인율 (%)
  
  // Images
  imageUrl: text("image_url").notNull(),
  thumbnailUrl: text("thumbnail_url"),
  imageUrls: text("image_urls").array().default(sql`'{}'::text[]`), // 추가 이미지
  
  // Contact Info
  contactName: text("contact_name"), // 담당자명
  contactPhone: text("contact_phone").notNull(), // 전화번호
  contactEmail: text("contact_email"), // 이메일
  website: text("website"), // 웹사이트 URL
  
  // Address
  address: text("address"), // 상세 주소
  city: text("city"), // 도시
  state: text("state"), // 주/구역
  postcode: text("postcode"), // 우편번호
  
  // Membership Tiers
  isPremiumOnly: boolean("is_premium_only").default(false), // 프리미엄 전용
  isPlatinumOnly: boolean("is_platinum_only").default(false), // 플래티넘 전용
  
  // Display Control
  status: text("status").notNull().default("pending"), // active, inactive, pending, rejected
  featured: boolean("featured").default(false), // 추천 상품 여부
  priority: integer("priority").default(5), // 1-10 (높을수록 우선 표시)
  
  // Validity Period
  startDate: text("start_date"), // YYYY-MM-DD
  endDate: text("end_date"), // YYYY-MM-DD
  
  // Stats
  views: integer("views").default(0),
  clicks: integer("clicks").default(0),
  
  // Tags
  tags: text("tags").array().default(sql`'{}'::text[]`),
  
  // Business Directory Integration (교민 업체주소록 연동)
  businessId: varchar("business_id"), // 연결된 업체 ID (Business Directory)
  
  // Metadata
  userId: varchar("user_id"), // 일반 사용자 등록자 (Firebase UID)
  createdBy: varchar("created_by"), // 관리자 등록자 (Admin UID)
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMarketplaceItemSchema = omitSchemaFields(createInsertSchema(marketplaceItems), {
  id: true,
  createdAt: true,
  updatedAt: true,
  views: true,
  clicks: true,
});

export type InsertMarketplaceItem = z.infer<typeof insertMarketplaceItemSchema>;
export type MarketplaceItem = typeof marketplaceItems.$inferSelect;

// Marketplace Coupons (쿠폰)
export const marketplaceCoupons = pgTable("marketplace_coupons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  itemId: varchar("item_id"), // 연결된 상품 ID (optional)
  businessName: text("business_name").notNull(),
  
  // Coupon Info
  title: text("title").notNull(), // 쿠폰명
  titleKo: text("title_ko"),
  code: text("code").notNull().unique(), // 쿠폰 코드
  description: text("description"),
  descriptionKo: text("description_ko"),
  
  // Discount
  discountType: text("discount_type").notNull(), // percentage, fixed
  discountValue: decimal("discount_value", { precision: 10, scale: 2 }).notNull(), // % or $
  minimumPurchase: decimal("minimum_purchase", { precision: 10, scale: 2 }), // 최소 구매 금액
  maximumDiscount: decimal("maximum_discount", { precision: 10, scale: 2 }), // 최대 할인 금액
  
  // Membership Restriction
  membershipRequired: text("membership_required").default("free"), // free, premium, platinum
  
  // Validity
  startDate: text("start_date").notNull(), // YYYY-MM-DD
  endDate: text("end_date").notNull(), // YYYY-MM-DD
  usageLimit: integer("usage_limit"), // 사용 횟수 제한
  usedCount: integer("used_count").default(0), // 사용된 횟수
  
  // Display
  status: text("status").notNull().default("active"), // active, inactive, expired
  featured: boolean("featured").default(false),
  
  // Metadata
  createdBy: varchar("created_by"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMarketplaceCouponSchema = omitSchemaFields(createInsertSchema(marketplaceCoupons), {
  id: true,
  createdAt: true,
  updatedAt: true,
  usedCount: true,
});

export type InsertMarketplaceCoupon = z.infer<typeof insertMarketplaceCouponSchema>;
export type MarketplaceCoupon = typeof marketplaceCoupons.$inferSelect;

// Marketplace Status types
export const marketplaceStatuses = [
  { id: "pending", label: "승인대기", labelEn: "Pending", color: "yellow" },
  { id: "active", label: "활성", labelEn: "Active", color: "green" },
  { id: "inactive", label: "비활성", labelEn: "Inactive", color: "gray" },
  { id: "rejected", label: "거부", labelEn: "Rejected", color: "red" },
  { id: "expired", label: "만료", labelEn: "Expired", color: "gray" },
] as const;

export type MarketplaceStatus = typeof marketplaceStatuses[number]["id"];

// Coupon Discount Types
export const couponDiscountTypes = [
  { id: "percentage", label: "할인율 (%)", labelEn: "Percentage" },
  { id: "fixed", label: "고정 금액 ($)", labelEn: "Fixed Amount" },
] as const;

export type CouponDiscountType = typeof couponDiscountTypes[number]["id"];

// ==========================================
// 🍽️ RESTAURANTS (맛집) SCHEMAS
// ==========================================

// Restaurant Categories
export const restaurantCategories = [
  { id: "korean", label: "한식", labelEn: "Korean", icon: "🇰🇷" },
  { id: "japanese", label: "일식", labelEn: "Japanese", icon: "🇯🇵" },
  { id: "chinese", label: "중식", labelEn: "Chinese", icon: "🇨🇳" },
  { id: "western", label: "양식", labelEn: "Western", icon: "🍝" },
  { id: "cafe", label: "카페/디저트", labelEn: "Cafe/Dessert", icon: "☕" },
  { id: "asian", label: "아시아음식", labelEn: "Asian", icon: "🍜" },
  { id: "fusion", label: "퓨전", labelEn: "Fusion", icon: "🍱" },
  { id: "bar", label: "주점/바", labelEn: "Bar/Pub", icon: "🍺" },
  { id: "buffet", label: "뷔페", labelEn: "Buffet", icon: "🍽️" },
  { id: "other", label: "기타", labelEn: "Other", icon: "🍴" },
] as const;

export type RestaurantCategoryId = typeof restaurantCategories[number]["id"];

// Price Range
export const priceRanges = [
  { id: "budget", label: "$", labelEn: "Budget", description: "$10 이하" },
  { id: "moderate", label: "$$", labelEn: "Moderate", description: "$10-30" },
  { id: "expensive", label: "$$$", labelEn: "Expensive", description: "$30-60" },
  { id: "luxury", label: "$$$$", labelEn: "Luxury", description: "$60 이상" },
] as const;

export type PriceRangeId = typeof priceRanges[number]["id"];

// Restaurants (맛집)
export const restaurants = pgTable("restaurants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Basic Info
  restaurantName: text("restaurant_name").notNull(), // 식당명
  restaurantNameKo: text("restaurant_name_ko").notNull(), // 한글명
  category: text("category").notNull(), // RestaurantCategoryId
  region: text("region").default("ALL"), // NSW, VIC, QLD, SA, WA, TAS, NT, ACT, ALL
  
  // Description
  shortDescription: text("short_description"), // 짧은 소개
  description: text("description"), // 상세 설명
  descriptionKo: text("description_ko"), // 한글 설명
  specialties: text("specialties").array().default(sql`'{}'::text[]`), // 대표 메뉴 (예: ["갈비탕", "냉면"])
  
  // Pricing
  priceRange: text("price_range").default("moderate"), // budget, moderate, expensive, luxury
  averagePrice: decimal("average_price", { precision: 10, scale: 2 }), // 평균 가격
  
  // Images
  logoUrl: text("logo_url"), // 로고
  coverImageUrl: text("cover_image_url"), // 커버 이미지
  imageUrls: text("image_urls").array().default(sql`'{}'::text[]`), // 추가 이미지
  
  // Contact Info
  contactPhone: text("contact_phone").notNull(), // 전화번호
  contactEmail: text("contact_email"), // 이메일
  website: text("website"), // 웹사이트
  facebook: text("facebook"), // Facebook URL
  instagram: text("instagram"), // Instagram URL
  kakaoId: text("kakao_id"), // 카카오톡 ID
  
  // Address
  address: text("address").notNull(), // 상세 주소
  city: text("city"), // 도시
  state: text("state"), // 주/구역
  postcode: text("postcode"), // 우편번호
  latitude: text("latitude"), // 위도
  longitude: text("longitude"), // 경도
  
  // Business Hours
  businessHours: text("business_hours"), // 영업 시간
  closedDays: text("closed_days").array().default(sql`'{}'::text[]`), // 휴무일
  
  // Features & Amenities
  features: text("features").array().default(sql`'{}'::text[]`), // 특징 (예: ["주차가능", "단체석", "예약가능"])
  cuisine: text("cuisine").array().default(sql`'{}'::text[]`), // 요리 종류
  dietaryOptions: text("dietary_options").array().default(sql`'{}'::text[]`), // 식단 옵션 (채식, 할랄 등)
  
  // Membership & Access
  membershipTier: text("membership_tier").default("free"), // free, premium, platinum
  isPremiumOnly: boolean("is_premium_only").default(false),
  isPlatinumOnly: boolean("is_platinum_only").default(false),
  
  // Display Control
  status: text("status").notNull().default("pending"), // active, inactive, pending, rejected
  featured: boolean("featured").default(false),
  priority: integer("priority").default(5), // 1-10
  
  // Stats & Reviews
  averageRating: decimal("average_rating", { precision: 3, scale: 2 }).default("0"),
  reviewCount: integer("review_count").default(0),
  views: integer("views").default(0),
  clicks: integer("clicks").default(0),
  bookmarks: integer("bookmarks").default(0),
  
  // Tags
  tags: text("tags").array().default(sql`'{}'::text[]`),
  
  // Verification
  isVerified: boolean("is_verified").default(false),
  isPremiumVerified: boolean("is_premium_verified").default(false),
  isPartnerVerified: boolean("is_partner_verified").default(false),
  
  // Business Directory Integration
  businessId: varchar("business_id"), // 연결된 업체 ID (Business Directory)
  
  // Metadata
  userId: varchar("user_id"), // 등록자 (Firebase UID)
  createdBy: varchar("created_by"), // 관리자 등록자
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertRestaurantSchema = omitSchemaFields(createInsertSchema(restaurants), {
  id: true,
  createdAt: true,
  updatedAt: true,
  views: true,
  clicks: true,
  bookmarks: true,
  reviewCount: true,
  averageRating: true,
});

export type InsertRestaurant = z.infer<typeof insertRestaurantSchema>;
export type Restaurant = typeof restaurants.$inferSelect;

// Restaurant Menus (메뉴)
export const restaurantMenus = pgTable("restaurant_menus", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  restaurantId: varchar("restaurant_id").notNull(), // 레스토랑 ID
  
  // Menu Info
  menuName: text("menu_name").notNull(), // 메뉴명
  menuNameKo: text("menu_name_ko"), // 한글명
  category: text("category").notNull(), // main, side, drink, dessert, etc.
  description: text("description"), // 설명
  descriptionKo: text("description_ko"),
  
  // Pricing
  price: decimal("price", { precision: 10, scale: 2 }).notNull(), // 가격
  originalPrice: decimal("original_price", { precision: 10, scale: 2 }), // 원가
  
  // Details
  imageUrl: text("image_url"), // 메뉴 이미지
  ingredients: text("ingredients").array().default(sql`'{}'::text[]`), // 재료
  allergens: text("allergens").array().default(sql`'{}'::text[]`), // 알레르기 유발 물질
  spicyLevel: integer("spicy_level"), // 매운 정도 (1-5)
  isVegetarian: boolean("is_vegetarian").default(false),
  isVegan: boolean("is_vegan").default(false),
  isGlutenFree: boolean("is_gluten_free").default(false),
  
  // Display
  isAvailable: boolean("is_available").default(true), // 현재 판매 여부
  isPopular: boolean("is_popular").default(false), // 인기 메뉴
  isRecommended: boolean("is_recommended").default(false), // 추천 메뉴
  displayOrder: integer("display_order").default(0), // 표시 순서
  
  // Metadata
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertRestaurantMenuSchema = omitSchemaFields(createInsertSchema(restaurantMenus), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertRestaurantMenu = z.infer<typeof insertRestaurantMenuSchema>;
export type RestaurantMenu = typeof restaurantMenus.$inferSelect;

// Menu Categories
export const menuCategories = [
  { id: "main", label: "메인 요리", labelEn: "Main Dish" },
  { id: "appetizer", label: "전채/애피타이저", labelEn: "Appetizer" },
  { id: "side", label: "사이드/반찬", labelEn: "Side Dish" },
  { id: "soup", label: "국/찌개", labelEn: "Soup/Stew" },
  { id: "noodle", label: "면 요리", labelEn: "Noodles" },
  { id: "rice", label: "밥/덮밥", labelEn: "Rice/Rice Bowl" },
  { id: "drink", label: "음료", labelEn: "Beverage" },
  { id: "dessert", label: "디저트", labelEn: "Dessert" },
  { id: "alcohol", label: "주류", labelEn: "Alcohol" },
  { id: "set", label: "세트 메뉴", labelEn: "Set Menu" },
] as const;

export type MenuCategoryId = typeof menuCategories[number]["id"];

// Restaurant Reviews
export const restaurantReviews = pgTable("restaurant_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  restaurantId: varchar("restaurant_id").notNull(),
  userId: varchar("user_id").notNull(),
  userDisplayName: text("user_display_name").notNull(),
  userPhotoUrl: text("user_photo_url"),
  
  // Review Content
  rating: integer("rating").notNull(), // 1-5
  title: text("title"),
  content: text("content").notNull(),
  
  // Additional Info
  visitDate: text("visit_date"), // YYYY-MM-DD
  imageUrls: text("image_urls").array().default(sql`'{}'::text[]`),
  
  // Metadata
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertRestaurantReviewSchema = omitSchemaFields(createInsertSchema(restaurantReviews), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertRestaurantReview = z.infer<typeof insertRestaurantReviewSchema>;
export type RestaurantReview = typeof restaurantReviews.$inferSelect;

// ==========================================
// 🏢 BUSINESS DIRECTORY (교민 업체주소록) SCHEMAS
// ==========================================

// Business Directory Categories
export const businessCategories = [
  { id: "restaurant", label: "식당/카페", labelEn: "Restaurant/Cafe", icon: "🍽️" },
  { id: "medical", label: "병원/치과/한의원", labelEn: "Medical/Dental/Clinic", icon: "🏥" },
  { id: "legal", label: "법률/이민", labelEn: "Legal/Immigration", icon: "⚖️" },
  { id: "accounting", label: "회계/세무", labelEn: "Accounting/Tax", icon: "💼" },
  { id: "realestate", label: "부동산", labelEn: "Real Estate", icon: "🏠" },
  { id: "education", label: "학원/교육", labelEn: "Education/Academy", icon: "🎓" },
  { id: "auto", label: "자동차", labelEn: "Automotive", icon: "🚗" },
  { id: "beauty", label: "미용/뷰티", labelEn: "Beauty/Salon", icon: "💄" },
  { id: "finance", label: "금융/보험", labelEn: "Finance/Insurance", icon: "💳" },
  { id: "moving", label: "이사/운송", labelEn: "Moving/Transport", icon: "🚚" },
  { id: "repair", label: "수리/시공", labelEn: "Repair/Construction", icon: "🔧" },
  { id: "travel", label: "여행/항공", labelEn: "Travel/Flight", icon: "✈️" },
  { id: "shopping", label: "쇼핑/유통", labelEn: "Shopping/Retail", icon: "🛒" },
  { id: "service", label: "기타 서비스", labelEn: "Other Services", icon: "📋" },
] as const;

export type BusinessCategoryId = typeof businessCategories[number]["id"];

// Business Directory (교민 업체)
export const businesses = pgTable("businesses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Basic Info
  businessName: text("business_name").notNull(), // 업체명 (영문)
  businessNameKo: text("business_name_ko").notNull(), // 업체명 (한글)
  category: text("category").notNull(), // BusinessCategoryId
  region: text("region").default("ALL"), // NSW, VIC, QLD, SA, WA, TAS, NT, ACT, ALL
  
  // Description (멤버십별 제한)
  shortDescription: text("short_description"), // 짧은 소개 (100자)
  description: text("description"), // 상세 설명 (Free: 100자, Premium: 300자, Platinum: 1000자)
  descriptionKo: text("description_ko"), // 한글 상세 설명
  
  // Images (멤버십별 제한)
  logoUrl: text("logo_url"), // 로고
  coverImageUrl: text("cover_image_url"), // 커버 이미지
  bannerImageUrl: text("banner_image_url"), // 배너 이미지 (Premium/Platinum 전용)
  imageUrls: text("image_urls").array().default(sql`'{}'::text[]`), // 추가 이미지 (Free: 1장, Premium: 3장, Platinum: 10장)
  
  // Contact Info
  ownerName: text("owner_name"), // 대표자명
  contactPhone: text("contact_phone").notNull(), // 전화번호
  contactEmail: text("contact_email"), // 이메일 (Premium 이상만 공개)
  website: text("website"), // 웹사이트 (Premium 이상만)
  facebook: text("facebook"), // Facebook URL
  instagram: text("instagram"), // Instagram URL
  youtubeUrl: text("youtube_url"), // YouTube 채널/영상 URL
  kakaoId: text("kakao_id"), // 카카오톡 ID
  
  // Address
  address: text("address").notNull(), // 상세 주소
  city: text("city"), // 도시
  state: text("state"), // 주/구역
  postcode: text("postcode"), // 우편번호
  latitude: text("latitude"), // 위도 (지도 표시용)
  longitude: text("longitude"), // 경도
  
  // Business Hours
  businessHours: text("business_hours"), // 영업시간 (JSON string)
  
  // Membership & Premium Features
  membershipTier: text("membership_tier").default("free"), // free, premium, platinum
  isPremiumListing: boolean("is_premium_listing").default(false), // 프리미엄 리스팅 여부
  isPlatinumListing: boolean("is_platinum_listing").default(false), // 플래티넘 리스팅 여부
  featuredUntil: timestamp("featured_until"), // 추천 업체 기간
  
  // Stats & Engagement
  views: integer("views").default(0), // 조회수
  clicks: integer("clicks").default(0), // 클릭수 (전화, 웹사이트 등)
  bookmarks: integer("bookmarks").default(0), // 찜 수
  averageRating: decimal("average_rating", { precision: 3, scale: 2 }).default("0"), // 평균 평점
  reviewCount: integer("review_count").default(0), // 리뷰 수
  
  // Related Products (핫샵 연동)
  hasProducts: boolean("has_products").default(false), // 핫샵 상품 보유 여부
  productCount: integer("product_count").default(0), // 연관 상품 수
  
  // Feature Icons (리스트 표시용 아이콘)
  hasMap: boolean("has_map").default(false), // 지도 정보 보유 여부 (위도/경도 있음)
  hasYouTube: boolean("has_youtube").default(false), // 유튜브 채널 보유 여부
  
  // Status & Approval
  status: text("status").notNull().default("pending"), // pending, active, inactive, rejected
  
  // 3-Tier Verification System (OZK 신뢰마크)
  isVerified: boolean("is_verified").default(false), // 🟢 OZK Verified (기본 인증)
  isPremiumVerified: boolean("is_premium_verified").default(false), // 🔵 프리미엄 인증
  isPartnerVerified: boolean("is_partner_verified").default(false), // 🟡 파트너 인증
  abnNumber: text("abn_number"), // Australian Business Number (ABN)
  verificationSubmittedAt: timestamp("verification_submitted_at"), // 인증 신청일
  verificationApprovedAt: timestamp("verification_approved_at"), // 인증 승인일
  verificationNotes: text("verification_notes"), // 인증 관련 관리자 메모
  
  // Tags & SEO
  tags: text("tags").array().default(sql`'{}'::text[]`),
  keywords: text("keywords"), // SEO 키워드
  
  // Metadata
  userId: varchar("user_id"), // 등록한 사용자 (Firebase UID)
  createdBy: varchar("created_by"), // 관리자 등록자
  submittedAt: timestamp("submitted_at").notNull().defaultNow(), // 등록 신청일
  approvedAt: timestamp("approved_at"), // 승인일
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertBusinessSchema = omitSchemaFields(createInsertSchema(businesses), {
  id: true,
  createdAt: true,
  updatedAt: true,
  views: true,
  clicks: true,
  bookmarks: true,
  averageRating: true,
  reviewCount: true,
  productCount: true,
  submittedAt: true,
  approvedAt: true,
});

export type InsertBusiness = z.infer<typeof insertBusinessSchema>;
export type Business = typeof businesses.$inferSelect;

// Business Reviews (업체 리뷰)
export const businessReviews = pgTable("business_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  businessId: varchar("business_id").notNull(), // 업체 ID
  userId: varchar("user_id").notNull(), // 리뷰 작성자 (Firebase UID)
  userDisplayName: text("user_display_name").notNull(),
  userPhotoUrl: text("user_photo_url"),
  
  // Review Content
  rating: integer("rating").notNull(), // 1-5 별점
  title: text("title"), // 리뷰 제목
  content: text("content").notNull(), // 리뷰 내용
  
  // Images
  imageUrls: text("image_urls").array().default(sql`'{}'::text[]`), // 리뷰 이미지
  
  // Response (사업자 답변 - Premium/Platinum만 가능)
  ownerResponse: text("owner_response"), // 사업자 답변
  respondedAt: timestamp("responded_at"), // 답변 작성일
  
  // Helpful votes
  helpfulCount: integer("helpful_count").default(0), // 도움됨 수
  
  // Status
  status: text("status").notNull().default("active"), // active, hidden, reported
  
  // Metadata
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertBusinessReviewSchema = omitSchemaFields(createInsertSchema(businessReviews), {
  id: true,
  createdAt: true,
  updatedAt: true,
  helpfulCount: true,
});

export type InsertBusinessReview = z.infer<typeof insertBusinessReviewSchema>;
export type BusinessReview = typeof businessReviews.$inferSelect;

// Business Status types
export const businessStatuses = [
  { id: "pending", label: "승인대기", labelEn: "Pending", color: "yellow" },
  { id: "active", label: "활성", labelEn: "Active", color: "green" },
  { id: "inactive", label: "비활성", labelEn: "Inactive", color: "gray" },
  { id: "rejected", label: "거부", labelEn: "Rejected", color: "red" },
] as const;

export type BusinessStatus = typeof businessStatuses[number]["id"];

// OZK Verification System (3-Tier)
export const verificationTypes = [
  {
    id: "basic",
    name: "OZK Verified",
    nameKo: "실제 사업체 확인",
    icon: "CheckCircle2",
    color: "green",
    description: "ABN 등록으로 검증된 실제 운영 중인 사업체",
    descriptionEn: "Verified business with confirmed ABN registration",
    requirements: [
      "사업자등록번호(ABN) 제출",
      "관리자 확인 완료",
      "실제 운영 중인 사업체"
    ]
  },
  {
    id: "premium",
    name: "Premium Verified",
    nameKo: "고객 만족도 검증",
    icon: "Star",
    color: "blue",
    description: "고객 만족도가 검증된 신뢰할 수 있는 업체입니다",
    descriptionEn: "Trusted business with verified customer satisfaction",
    requirements: [
      "고객 리뷰 5건 이상",
      "평균 평점 ★4.5 이상",
      "3개월 이상 활동",
      "OZK Verified 보유"
    ]
  },
  {
    id: "partner",
    name: "Partner Verified",
    nameKo: "파트너십 검증",
    icon: "Shield",
    color: "amber",
    description: "OZK News와 계약을 맺은 공식 파트너",
    descriptionEn: "Official OZK News partner with verified credentials",
    requirements: [
      "유료 광고/제휴 계약",
      "실물 계약서 검증",
      "OZK News 공식 파트너"
    ]
  }
] as const;

export type VerificationTypeId = typeof verificationTypes[number]["id"];

// YouTube Videos Schema
export const videos = pgTable("videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Content
  title: text("title").notNull(),
  titleKo: text("title_ko"),
  description: text("description"),
  descriptionKo: text("description_ko"),
  
  // YouTube Data
  youtubeId: text("youtube_id").notNull().unique(), // YouTube video ID (e.g., "dQw4w9WgXcQ")
  youtubeUrl: text("youtube_url").notNull(), // Full YouTube URL
  thumbnailUrl: text("thumbnail_url"),
  
  // Categorization
  category: text("category").notNull(), // stock-korea, stock-usa, stock-australia, realestate, news-summary, interview, business-review
  region: text("region").default("ALL"), // For realestate: NSW, VIC, QLD, SA, WA, TAS, NT, ACT, ALL
  tags: text("tags").array().default(sql`'{}'::text[]`),
  
  // Metadata
  duration: integer("duration"), // Duration in seconds
  views: integer("views").default(0),
  likes: integer("likes").default(0),
  
  // Status
  status: text("status").notNull().default("published"), // draft, published, unlisted
  isFeatured: boolean("is_featured").default(false), // 추천 영상
  isShorts: boolean("is_shorts").default(false), // YouTube Shorts 여부
  
  // Timestamps
  publishedAt: timestamp("published_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertVideoSchema = omitSchemaFields(createInsertSchema(videos), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertVideo = z.infer<typeof insertVideoSchema>;
export type Video = typeof videos.$inferSelect;

// Video Categories
export const videoCategories = [
  { id: "stock-korea", label: "한국 주식 시황", labelEn: "Korea Stock Market", icon: "TrendingUp" },
  { id: "stock-usa", label: "미국 주식 시황", labelEn: "US Stock Market", icon: "TrendingUp" },
  { id: "stock-australia", label: "호주 주식 시황", labelEn: "Australia Stock Market", icon: "TrendingUp" },
  { id: "realestate", label: "부동산 분석", labelEn: "Real Estate Analysis", icon: "Home" },
  { id: "news-summary", label: "뉴스 요약", labelEn: "News Summary", icon: "Newspaper" },
  { id: "interview", label: "교민 인터뷰", labelEn: "Community Interview", icon: "Users" },
  { id: "business-review", label: "비즈니스 리뷰", labelEn: "Business Review", icon: "Briefcase" },
] as const;

export type VideoCategoryId = typeof videoCategories[number]["id"];

// Video Status types
export const videoStatuses = [
  { id: "draft", label: "임시저장", labelEn: "Draft", color: "gray" },
  { id: "published", label: "게시됨", labelEn: "Published", color: "green" },
  { id: "unlisted", label: "비공개", labelEn: "Unlisted", color: "yellow" },
] as const;

export type VideoStatus = typeof videoStatuses[number]["id"];

// ============================================
// Community Market (중고거래 + 개인광고)
// ============================================

// Market Listing Categories
export const marketCategories = [
  { id: "electronics", label: "전자제품", labelEn: "Electronics", icon: "Smartphone" },
  { id: "furniture", label: "가구/인테리어", labelEn: "Furniture", icon: "Sofa" },
  { id: "clothing", label: "의류/잡화", labelEn: "Clothing", icon: "Shirt" },
  { id: "books", label: "도서/교육", labelEn: "Books", icon: "BookOpen" },
  { id: "sports", label: "스포츠/레저", labelEn: "Sports", icon: "Dumbbell" },
  { id: "baby", label: "유아용품", labelEn: "Baby Items", icon: "Baby" },
  { id: "beauty", label: "화장품/미용", labelEn: "Beauty", icon: "Sparkles" },
  { id: "home", label: "생활용품", labelEn: "Home Goods", icon: "Home" },
  { id: "car", label: "자동차/오토바이", labelEn: "Vehicles", icon: "Car" },
  { id: "service", label: "서비스", labelEn: "Services", icon: "Briefcase" },
  { id: "tutor", label: "과외/레슨", labelEn: "Tutoring", icon: "GraduationCap" },
  { id: "job", label: "구인/구직", labelEn: "Jobs", icon: "Users" },
  { id: "roommate", label: "룸메이트", labelEn: "Roommate", icon: "Home" },
  { id: "rental", label: "단기렌트", labelEn: "Short-term Rental", icon: "Key" },
  { id: "event", label: "행사/모임", labelEn: "Events", icon: "Calendar" },
  { id: "other", label: "기타", labelEn: "Other", icon: "MoreHorizontal" },
] as const;

export type MarketCategoryId = typeof marketCategories[number]["id"];

// Australian Cities by State (주요 대도시 + Suburb)
export const australianCities = {
  NSW: [
    { id: "sydney", label: "시드니", labelEn: "Sydney", suburbs: [
      "CBD", "Chatswood", "Parramatta", "Strathfield", "Eastwood", 
      "Epping", "Hornsby", "Bankstown", "Liverpool", "Bondi",
      "Manly", "North Sydney", "Pyrmont", "Surry Hills", "Newtown"
    ]},
    { id: "newcastle", label: "뉴캐슬", labelEn: "Newcastle", suburbs: [] },
    { id: "wollongong", label: "울릉공", labelEn: "Wollongong", suburbs: [] },
  ],
  VIC: [
    { id: "melbourne", label: "멜번", labelEn: "Melbourne", suburbs: [
      "CBD", "Box Hill", "Clayton", "Glen Waverley", "Doncaster",
      "Springvale", "Footscray", "Preston", "Brunswick", "Carlton"
    ]},
    { id: "geelong", label: "질롱", labelEn: "Geelong", suburbs: [] },
  ],
  QLD: [
    { id: "brisbane", label: "브리즈번", labelEn: "Brisbane", suburbs: [
      "CBD", "Sunnybank", "Southbank", "Fortitude Valley", "Chermside"
    ]},
    { id: "gold-coast", label: "골드코스트", labelEn: "Gold Coast", suburbs: [] },
    { id: "cairns", label: "케언스", labelEn: "Cairns", suburbs: [] },
  ],
  SA: [
    { id: "adelaide", label: "애들레이드", labelEn: "Adelaide", suburbs: ["CBD"] },
  ],
  WA: [
    { id: "perth", label: "퍼스", labelEn: "Perth", suburbs: ["CBD"] },
  ],
  TAS: [
    { id: "hobart", label: "호바트", labelEn: "Hobart", suburbs: [] },
  ],
  NT: [
    { id: "darwin", label: "다윈", labelEn: "Darwin", suburbs: [] },
  ],
  ACT: [
    { id: "canberra", label: "캔버라", labelEn: "Canberra", suburbs: [] },
  ],
} as const;

// Market Listing Status
export const marketStatuses = [
  { id: "available", label: "판매중", labelEn: "Available", color: "green" },
  { id: "reserved", label: "예약중", labelEn: "Reserved", color: "yellow" },
  { id: "sold", label: "거래완료", labelEn: "Sold", color: "gray" },
] as const;

export type MarketStatus = typeof marketStatuses[number]["id"];

// Market Listings Schema
export const marketListings = pgTable("market_listings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // Firebase UID
  userEmail: text("user_email").notNull(),
  userDisplayName: text("user_display_name").notNull(),
  userPhotoUrl: text("user_photo_url"),
  
  // Content
  title: text("title").notNull(),
  description: text("description").notNull(),
  priceCents: integer("price_cents").notNull(), // AUD cents (0 = 무료나눔, -1 = 가격협의)
  category: text("category").notNull(),
  condition: text("condition"), // new, like-new, good, fair, poor
  
  // Location (3-level system)
  state: text("state").notNull(), // NSW, VIC, QLD, etc.
  city: text("city").notNull(), // sydney, melbourne, etc.
  suburb: text("suburb"), // Optional: Chatswood, Parramatta, etc.
  postcode: text("postcode"), // Optional
  latitude: decimal("latitude", { precision: 10, scale: 7 }), // GPS 좌표
  longitude: decimal("longitude", { precision: 10, scale: 7 }), // GPS 좌표
  
  // Images
  images: text("images").array().default(sql`'{}'::text[]`), // Max 10 images
  
  // Contact & Delivery
  contactMethod: text("contact_method").notNull(), // phone, email, both, kakao
  phoneNumber: text("phone_number"),
  kakaoUrl: text("kakao_url"), // Kakao Open Chat URL for instant messaging
  deliveryAvailable: boolean("delivery_available").default(false),
  pickupLocations: text("pickup_locations"), // "Chatswood, Epping 가능"
  
  // Status & Features
  status: text("status").notNull().default("available"), // available, reserved, sold
  isPremium: boolean("is_premium").default(false), // 유료 상단 고정
  premiumUntil: timestamp("premium_until"),
  
  // Analytics
  views: integer("views").default(0),
  favorites: integer("favorites").default(0),
  
  // Auto-expire (기본 7일)
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMarketListingSchema = omitSchemaFields(createInsertSchema(marketListings), {
  id: true,
  createdAt: true,
  updatedAt: true,
  expiresAt: true,
  premiumUntil: true,
}).extend({
  expiresAt: z.union([z.date(), z.string()]).transform((val) => 
    typeof val === 'string' ? new Date(val) : val
  ),
  premiumUntil: z.union([z.date(), z.string(), z.undefined(), z.null()]).transform((val) => 
    val ? (typeof val === 'string' ? new Date(val) : val) : undefined
  ).optional().nullable(),
});

export type InsertMarketListing = z.infer<typeof insertMarketListingSchema>;
export type MarketListing = typeof marketListings.$inferSelect;

// Market Favorites/Bookmarks
export const marketFavorites = pgTable("market_favorites", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  listingId: varchar("listing_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertMarketFavoriteSchema = omitSchemaFields(createInsertSchema(marketFavorites), {
  id: true,
  createdAt: true,
});

export type InsertMarketFavorite = z.infer<typeof insertMarketFavoriteSchema>;
export type MarketFavorite = typeof marketFavorites.$inferSelect;

// Market Reviews (거래 후기/평가)
export const marketReviews = pgTable("market_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  listingId: varchar("listing_id").notNull(), // 관련 거래 상품
  
  // Reviewer & Reviewee
  reviewerId: varchar("reviewer_id").notNull(), // 리뷰 작성자 (Firebase UID)
  reviewerName: text("reviewer_name").notNull(),
  reviewerPhoto: text("reviewer_photo"),
  
  revieweeId: varchar("reviewee_id").notNull(), // 리뷰 받는 사람 (Firebase UID)
  revieweeName: text("reviewee_name").notNull(),
  revieweePhoto: text("reviewee_photo"),
  
  // Review Content
  rating: integer("rating").notNull(), // 1-5 stars
  comment: text("comment"), // Optional review text
  reviewType: text("review_type").notNull(), // buyer_to_seller, seller_to_buyer
  
  // Transaction Details
  transactionCompleted: boolean("transaction_completed").default(true),
  
  // Metadata
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMarketReviewSchema = omitSchemaFields(createInsertSchema(marketReviews), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertMarketReview = z.infer<typeof insertMarketReviewSchema>;
export type MarketReview = typeof marketReviews.$inferSelect;

// Review Types
export const reviewTypes = [
  { id: "buyer_to_seller", label: "구매자 → 판매자", labelEn: "Buyer to Seller" },
  { id: "seller_to_buyer", label: "판매자 → 구매자", labelEn: "Seller to Buyer" },
] as const;

export type ReviewType = typeof reviewTypes[number]["id"];

// Market Listing Status
export const marketListingStatuses = [
  { id: "available", label: "판매중", labelEn: "Available", color: "green" },
  { id: "reserved", label: "예약중", labelEn: "Reserved", color: "yellow" },
  { id: "sold", label: "거래완료", labelEn: "Sold", color: "gray" },
] as const;

export type MarketListingStatus = typeof marketListingStatuses[number]["id"];

// ==========================================
// 📍 CITY CLASSIFIEDS (도시별 개인광고)
// ==========================================

// City Classifieds Categories
export const cityClassifiedCategories = [
  { id: "services", label: "서비스", labelEn: "Services" },
  { id: "lessons", label: "레슨/과외", labelEn: "Lessons" },
  { id: "events", label: "이벤트/모임", labelEn: "Events" },
  { id: "event", label: "모임", labelEn: "Meetup" },
  { id: "housing", label: "부동산/쉐어", labelEn: "Housing" },
  { id: "vehicles", label: "중고차", labelEn: "Vehicles" },
  { id: "jobs", label: "구인/구직", labelEn: "Jobs" },
  { id: "childcare", label: "육아/보육", labelEn: "Childcare" },
  { id: "community", label: "커뮤니티", labelEn: "Community" },
  { id: "announcements", label: "공지사항", labelEn: "Announcements" },
  { id: "other", label: "기타", labelEn: "Other" },
] as const;

export type CityClassifiedCategory = typeof cityClassifiedCategories[number]["id"];

// City Classifieds Schema
export const cityClassifieds = pgTable("city_classifieds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // Firebase UID
  userEmail: text("user_email").notNull(),
  userDisplayName: text("user_display_name").notNull(),
  userPhotoUrl: text("user_photo_url"),
  
  // Content
  title: text("title").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(),
  subcategory: text("subcategory"), // Optional: for meetup categories (sports, friends, etc.)
  
  // Location
  state: text("state").notNull(), // NSW, VIC, QLD, etc.
  city: text("city").notNull(), // sydney, melbourne, etc.
  suburb: text("suburb"), // Optional: Chatswood, Parramatta, etc.
  
  // Contact
  contactMethod: text("contact_method").notNull(), // phone, email, both
  phoneNumber: text("phone_number"),
  email: text("email"),
  websiteUrl: text("website_url"),
  
  // Optional Features
  images: text("images").array().default(sql`'{}'::text[]`), // Max 3 images
  isPremium: boolean("is_premium").default(false), // 유료 상단 고정
  premiumUntil: timestamp("premium_until"),
  
  // Analytics
  views: integer("views").default(0),
  
  // Auto-expire (기본 30일)
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertCityClassifiedSchema = omitSchemaFields(createInsertSchema(cityClassifieds), {
  id: true,
  createdAt: true,
  updatedAt: true,
  expiresAt: true,
  premiumUntil: true,
}).extend({
  expiresAt: z.union([z.date(), z.string()]).transform((val) => 
    typeof val === 'string' ? new Date(val) : val
  ),
  premiumUntil: z.union([z.date(), z.string(), z.undefined(), z.null()]).transform((val) => 
    val ? (typeof val === 'string' ? new Date(val) : val) : undefined
  ).optional().nullable(),
});

export type InsertCityClassified = z.infer<typeof insertCityClassifiedSchema>;
export type CityClassified = typeof cityClassifieds.$inferSelect;

// ==========================================
// 🔄 UNIFIED MARKET (통합 마켓)
// ==========================================

// Unified Market Item - combines market listings and classifieds
export type UnifiedMarketItem = {
  id: string;
  type: 'trade' | 'ad'; // 'trade' = 거래글 (💸), 'ad' = 광고글 (📢)
  sourceType?: 'used' | 'car' | 'property' | 'classified' | 'talk';
  detailUrl?: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  userPhotoUrl?: string | null;
  
  // Content
  title: string;
  description: string;
  category: string;
  subcategory?: string | null; // For meetup events (sports, friends, outdoor, etc.)
  
  // Price (for trade items only)
  priceCents?: number | null; // null/undefined for ads
  pricePeriod?: string | null;
  propertyType?: 'sale' | 'rent' | string | null;
  listingType?: string | null;
  roomType?: string | null;
  condition?: string | null; // new, like-new, good, fair, poor (trade only)
  
  // Location
  state: string;
  city: string;
  suburb?: string | null;
  postcode?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  
  // Images
  images: string[];
  
  // Contact
  contactMethod: string;
  phoneNumber?: string | null;
  email?: string | null;
  websiteUrl?: string | null;
  deliveryAvailable?: boolean;
  pickupLocations?: string | null;
  
  // Status
  status?: string; // available, reserved, sold (trade only)
  isPremium: boolean;
  premiumUntil?: Date | null;
  
  // Analytics
  views: number;
  favorites?: number; // trade only
  
  // Metadata
  expiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
};

// Combined categories for unified market
export const unifiedMarketCategories = [
  // From marketCategories (중고거래)
  { id: "electronics", label: "전자제품", labelEn: "Electronics", icon: "Smartphone", type: "trade" },
  { id: "furniture", label: "가구/인테리어", labelEn: "Furniture", icon: "Sofa", type: "trade" },
  { id: "clothing", label: "의류/잡화", labelEn: "Clothing", icon: "Shirt", type: "trade" },
  { id: "books", label: "도서/교육", labelEn: "Books", icon: "BookOpen", type: "trade" },
  { id: "sports", label: "스포츠/레저", labelEn: "Sports", icon: "Dumbbell", type: "trade" },
  { id: "baby", label: "유아용품", labelEn: "Baby Items", icon: "Baby", type: "trade" },
  { id: "beauty", label: "화장품/미용", labelEn: "Beauty", icon: "Sparkles", type: "trade" },
  { id: "home", label: "생활용품", labelEn: "Home Goods", icon: "Home", type: "trade" },
  { id: "car", label: "자동차/오토바이", labelEn: "Vehicles", icon: "Car", type: "trade" },
  
  // From cityClassifiedCategories (개인광고)
  { id: "service", label: "서비스", labelEn: "Services", icon: "Briefcase", type: "ad" },
  { id: "tutor", label: "과외/레슨", labelEn: "Tutoring", icon: "GraduationCap", type: "ad" },
  { id: "job", label: "구인/구직", labelEn: "Jobs", icon: "Users", type: "ad" },
  { id: "roommate", label: "룸메이트", labelEn: "Roommate", icon: "Home", type: "ad" },
  { id: "rental", label: "단기렌트", labelEn: "Short-term Rental", icon: "Key", type: "ad" },
  { id: "event", label: "행사/모임", labelEn: "Events", icon: "Calendar", type: "ad" },
  { id: "childcare", label: "육아/보육", labelEn: "Childcare", icon: "Baby", type: "ad" },
  { id: "community", label: "커뮤니티", labelEn: "Community", icon: "Users", type: "ad" },
  { id: "announcements", label: "공지사항", labelEn: "Announcements", icon: "Megaphone", type: "ad" },
  { id: "other", label: "기타", labelEn: "Other", icon: "MoreHorizontal", type: "both" },
] as const;

export type UnifiedMarketCategoryId = typeof unifiedMarketCategories[number]["id"];

// ==========================================
// 🛡️ SAFETY & COMPLIANCE SCHEMAS
// ==========================================

// User Agreements (약관 동의 기록)
export const userAgreements = pgTable("user_agreements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // Firebase UID
  
  // Agreement Types
  termsOfService: boolean("terms_of_service").notNull().default(false), // 이용약관
  privacyPolicy: boolean("privacy_policy").notNull().default(false), // 개인정보처리방침
  marketplaceTerms: boolean("marketplace_terms").default(false), // 마켓플레이스 약관
  communityGuidelines: boolean("community_guidelines").default(false), // 커뮤니티 가이드라인
  
  // Marketing & Communication
  marketingConsent: boolean("marketing_consent").default(false), // 마케팅 수신 동의
  smsConsent: boolean("sms_consent").default(false), // SMS 수신 동의
  emailConsent: boolean("email_consent").default(false), // 이메일 수신 동의
  
  // Age Verification (호주 법 - 18세 이상)
  ageVerified: boolean("age_verified").notNull().default(false), // 18세 이상 확인
  
  // IP & Location for Audit Trail
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  
  // Version Tracking
  termsVersion: text("terms_version").default("1.0"), // 동의한 약관 버전
  
  // Timestamps
  agreedAt: timestamp("agreed_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertUserAgreementSchema = omitSchemaFields(createInsertSchema(userAgreements), {
  id: true,
  agreedAt: true,
  updatedAt: true,
});

export type InsertUserAgreement = z.infer<typeof insertUserAgreementSchema>;
export type UserAgreement = typeof userAgreements.$inferSelect;

// Reports (신고 시스템)
export const reports = pgTable("reports", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Reporter Info
  reporterId: varchar("reporter_id").notNull(), // Firebase UID
  reporterEmail: text("reporter_email"),
  
  // Reported Content
  reportedType: text("reported_type").notNull(), // article, comment, marketplace, market_listing, business, user
  reportedId: varchar("reported_id").notNull(), // ID of reported content
  reportedUserId: varchar("reported_user_id"), // User who created the content
  
  // Report Details
  reason: text("reason").notNull(), // spam, scam, inappropriate, copyright, other
  category: text("category").notNull(), // fraud, harassment, fake, offensive, illegal
  description: text("description").notNull(), // 상세 설명
  evidenceUrls: text("evidence_urls").array().default(sql`'{}'::text[]`), // 증거 이미지/스크린샷
  
  // Status & Resolution
  status: text("status").notNull().default("pending"), // pending, reviewing, resolved, dismissed
  priority: text("priority").default("normal"), // low, normal, high, urgent
  assignedTo: varchar("assigned_to"), // Admin UID
  resolution: text("resolution"), // 처리 내용
  actionTaken: text("action_taken"), // warning, content_removed, user_suspended, user_banned, no_action
  
  // Admin Notes (내부용)
  adminNotes: text("admin_notes"),
  
  // Timestamps
  reportedAt: timestamp("reported_at").notNull().defaultNow(),
  reviewedAt: timestamp("reviewed_at"),
  resolvedAt: timestamp("resolved_at"),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertReportSchema = omitSchemaFields(createInsertSchema(reports), {
  id: true,
  reportedAt: true,
  reviewedAt: true,
  resolvedAt: true,
  updatedAt: true,
});

export type InsertReport = z.infer<typeof insertReportSchema>;
export type Report = typeof reports.$inferSelect;

// Report Reasons
export const reportReasons = [
  { id: "spam", label: "스팸", labelEn: "Spam", description: "홍보성 또는 반복적인 내용" },
  { id: "scam", label: "사기", labelEn: "Scam", description: "사기 의심 거래" },
  { id: "inappropriate", label: "부적절한 콘텐츠", labelEn: "Inappropriate Content", description: "욕설, 음란물 등" },
  { id: "copyright", label: "저작권 침해", labelEn: "Copyright Infringement", description: "무단 도용" },
  { id: "fake", label: "허위 정보", labelEn: "Fake Information", description: "거짓 정보 유포" },
  { id: "harassment", label: "괴롭힘", labelEn: "Harassment", description: "개인 공격 또는 괴롭힘" },
  { id: "illegal", label: "불법", labelEn: "Illegal", description: "불법 상품 또는 서비스" },
  { id: "other", label: "기타", labelEn: "Other", description: "기타 사유" },
] as const;

export type ReportReason = typeof reportReasons[number]["id"];

// Report Status
export const reportStatuses = [
  { id: "pending", label: "대기중", labelEn: "Pending", color: "yellow" },
  { id: "reviewing", label: "검토중", labelEn: "Reviewing", color: "blue" },
  { id: "resolved", label: "처리완료", labelEn: "Resolved", color: "green" },
  { id: "dismissed", label: "기각", labelEn: "Dismissed", color: "gray" },
] as const;

export type ReportStatus = typeof reportStatuses[number]["id"];

// Community Talk (동네톡톡)
export const communityTalk = pgTable("community_talk", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Content
  title: text("title").notNull(),
  content: text("content").notNull(),
  category: text("category").notNull(), // restaurant, event, pet, sports, etc.
  
  // Author Info
  authorId: varchar("author_id").notNull(), // Firebase UID
  authorNickname: text("author_nickname").notNull(),
  authorPostCount: integer("author_post_count").default(0), // 작성한 게시글 수
  authorSalesCount: integer("author_sales_count").default(0), // 완료한 판매 건수
  
  // Location
  region: text("region").notNull(), // NSW, VIC, QLD, etc.
  suburb: text("suburb").notNull(), // Eastwood, Epping, etc.
  
  // Images
  imageUrls: text("image_urls").array().default(sql`'{}'::text[]`),
  
  // Engagement
  likes: integer("likes").default(0),
  commentCount: integer("comment_count").default(0),
  views: integer("views").default(0),
  
  // Timestamps
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertCommunityTalkSchema = omitSchemaFields(createInsertSchema(communityTalk), {
  id: true,
  likes: true,
  commentCount: true,
  views: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertCommunityTalk = z.infer<typeof insertCommunityTalkSchema>;
export type CommunityTalk = typeof communityTalk.$inferSelect;

// Community Talk Categories
export const communityTalkCategories = [
  { id: 'restaurant', label: '맛집' },
  { id: 'event', label: '동네행사' },
  { id: 'pet', label: '반려동물' },
  { id: 'sports', label: '운동' },
  { id: 'convenience', label: '생활/편의' },
  { id: 'lost', label: '분실/실종' },
  { id: 'hospital', label: '병원/약국' },
  { id: 'story', label: '고민/사연' },
  { id: 'friends', label: '동네친구' },
  { id: 'moving', label: '이사/시공' },
  { id: 'housing', label: '주거/부동산' },
  { id: 'education', label: '교육' },
  { id: 'hobby', label: '취미' },
  { id: 'incident', label: '동네사건사고' },
  { id: 'scenery', label: '동네풍경' },
  { id: 'beauty', label: '미용' },
  { id: 'pregnancy', label: '임신/육아' },
  { id: 'general', label: '일반' },
] as const;

export type CommunityTalkCategory = typeof communityTalkCategories[number]["id"];

/* ========================= HMAC Device Authentication ========================= */

// Device Secrets (로컬 봇 디바이스 인증)
export const deviceSecrets = pgTable("device_secrets", {
  deviceId: varchar("device_id", { length: 100 }).primaryKey(),
  secret: varchar("secret", { length: 128 }).notNull(), // 64-char hex HMAC secret
  owner: text("owner"), // Device owner name (e.g., "philip")
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDeviceSecretSchema = omitSchemaFields(createInsertSchema(deviceSecrets), {
  createdAt: true,
});

export type InsertDeviceSecret = z.infer<typeof insertDeviceSecretSchema>;
export type DeviceSecret = typeof deviceSecrets.$inferSelect;

// Device Nonces (Replay Attack 방지)
export const deviceNonces = pgTable("device_nonces", {
  deviceId: varchar("device_id", { length: 100 }).notNull(),
  nonce: varchar("nonce", { length: 128 }).notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  pk: sql`PRIMARY KEY (${table.deviceId}, ${table.nonce})`,
}));

export const insertDeviceNonceSchema = omitSchemaFields(createInsertSchema(deviceNonces), {
  createdAt: true,
});

export type InsertDeviceNonce = z.infer<typeof insertDeviceNonceSchema>;
export type DeviceNonce = typeof deviceNonces.$inferSelect;

/* ========================= MEETINGS (모임) ========================= */

// Meetings - 정기/비정기 모임 관리
export const meetings = pgTable("meetings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // 기본 정보
  title: text("title").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(), // hobby, study, sports, food, culture, business, etc.
  
  // 위치 정보
  state: text("state"), // NSW, VIC, QLD, etc.
  city: text("city"),
  suburb: text("suburb"),
  location: text("location"), // 상세 장소 (예: "Strathfield Library")
  
  // 일정 정보
  schedule: text("schedule").notNull(), // one-time, weekly, biweekly, monthly
  nextMeetingDate: timestamp("next_meeting_date"),
  meetingTime: text("meeting_time"), // "19:00" 형식
  
  // 모임 상세
  capacity: integer("capacity"), // 최대 인원
  currentMembers: integer("current_members").default(0),
  fee: text("fee"), // "무료", "$10/회", etc.
  
  // 카카오톡 연동
  kakaoUrl: text("kakao_url"), // 오픈채팅 링크
  kakaoClicks: integer("kakao_clicks").default(0),
  
  // 이미지
  coverImage: text("cover_image"),
  photos: text("photos").array().default(sql`'{}'::text[]`), // R2 이미지 링크
  
  // 작성자
  hostId: text("host_id").notNull(),
  hostName: text("host_name").notNull(),
  hostEmail: text("host_email"),
  
  // 상태
  status: text("status").notNull().default("active"), // active, paused, cancelled, completed
  isPremium: boolean("is_premium").default(false),
  premiumUntil: timestamp("premium_until"),
  
  // 통계
  views: integer("views").default(0),
  postCount: integer("post_count").default(0),
  
  // 타임스탬프
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMeetingSchema = omitSchemaFields(createInsertSchema(meetings), {
  id: true,
  kakaoClicks: true,
  currentMembers: true,
  views: true,
  postCount: true,
  createdAt: true,
  updatedAt: true,
});

export type Meeting = typeof meetings.$inferSelect;
export type InsertMeeting = z.infer<typeof insertMeetingSchema>;

// Meeting Reviews (모임 후기/평점)
export const meetingReviews = pgTable("meeting_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  meetingId: varchar("meeting_id").notNull(), // 관련 모임
  
  // Reviewer
  reviewerId: varchar("reviewer_id").notNull(), // 리뷰 작성자 (Firebase UID)
  reviewerName: text("reviewer_name").notNull(),
  reviewerPhoto: text("reviewer_photo"),
  
  // Review Content
  rating: integer("rating").notNull(), // 1-5 stars
  comment: text("comment"), // Optional review text
  
  // Metadata
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMeetingReviewSchema = omitSchemaFields(createInsertSchema(meetingReviews), {
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type MeetingReview = typeof meetingReviews.$inferSelect;
export type InsertMeetingReview = z.infer<typeof insertMeetingReviewSchema>;

// Meeting Posts - 모임 게시판 (공지, 후기, 질문 등)
export const meetingPosts = pgTable("meeting_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  meetingId: varchar("meeting_id").notNull().references(() => meetings.id, { onDelete: "cascade" }),
  
  // 작성자
  authorId: text("author_id").notNull(),
  authorName: text("author_name").notNull(),
  authorEmail: text("author_email"),
  authorPhoto: text("author_photo"),
  
  // 내용
  type: text("type").notNull().default("general"), // notice, review, question, photo, general
  title: text("title"),
  content: text("content").notNull(),
  images: text("images").array().default(sql`'{}'::text[]`),
  
  // 상태
  isPinned: boolean("is_pinned").default(false),
  
  // 통계
  likes: integer("likes").default(0),
  
  // 타임스탬프
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMeetingPostSchema = omitSchemaFields(createInsertSchema(meetingPosts), {
  id: true,
  likes: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertMeetingPost = z.infer<typeof insertMeetingPostSchema>;
export type MeetingPost = typeof meetingPosts.$inferSelect;

// Meeting Participants - 모임 참가자 관리
export const meetingParticipants = pgTable("meeting_participants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  meetingId: varchar("meeting_id").notNull().references(() => meetings.id, { onDelete: "cascade" }),
  
  // 참가자 정보
  userId: text("user_id").notNull(),
  userName: text("user_name").notNull(),
  userEmail: text("user_email"),
  userPhoto: text("user_photo"),
  
  // 참가 상태
  status: text("status").notNull().default("active"), // active, inactive, banned
  role: text("role").notNull().default("member"), // host, moderator, member
  
  // 타임스탬프
  joinedAt: timestamp("joined_at").notNull().defaultNow(),
});

export const insertMeetingParticipantSchema = omitSchemaFields(createInsertSchema(meetingParticipants), {
  id: true,
  joinedAt: true,
});

export type InsertMeetingParticipant = z.infer<typeof insertMeetingParticipantSchema>;
export type MeetingParticipant = typeof meetingParticipants.$inferSelect;

// Kakao Click Tracking - 카카오톡 리다이렉트 클릭 추적
export const kakaoClicks = pgTable("kakao_clicks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Target Reference
  type: text("type").notNull(), // meeting, item, community_talk 등
  targetId: varchar("target_id").notNull(), // 대상 ID
  targetTitle: text("target_title"), // 대상 제목 (로그용)
  
  // User Info (선택)
  userId: varchar("user_id"), // 로그인한 경우 Firebase UID
  userEmail: varchar("user_email"), // 로그인한 경우 이메일
  
  // Request Info
  referer: text("referer"), // 어디서 클릭했는지
  userAgent: text("user_agent"), // 브라우저 정보
  ipHash: varchar("ip_hash"), // IP 해시 (개인정보 보호)
  
  // Location
  region: text("region"), // NSW, VIC 등
  city: text("city"), // Sydney, Melbourne 등
  suburb: text("suburb"), // Eastwood, Strathfield 등
  
  // Kakao URL
  kakaoUrl: text("kakao_url").notNull(), // 실제 리다이렉트된 카카오 URL
  
  // Metadata
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertKakaoClickSchema = omitSchemaFields(createInsertSchema(kakaoClicks), {
  id: true,
  createdAt: true,
});

export type InsertKakaoClick = z.infer<typeof insertKakaoClickSchema>;
export type KakaoClick = typeof kakaoClicks.$inferSelect;

// Meeting Categories
export const meetingCategories = [
  { id: "hobby", label: "취미/여가", icon: "🎨" },
  { id: "study", label: "스터디", icon: "📚" },
  { id: "sports", label: "운동/스포츠", icon: "⚽" },
  { id: "food", label: "맛집/식도락", icon: "🍽️" },
  { id: "culture", label: "문화/예술", icon: "🎭" },
  { id: "business", label: "비즈니스/네트워킹", icon: "💼" },
  { id: "parenting", label: "육아/교육", icon: "👶" },
  { id: "volunteer", label: "봉사/나눔", icon: "🤝" },
  { id: "travel", label: "여행/나들이", icon: "✈️" },
  { id: "language", label: "언어교환", icon: "🗣️" },
  { id: "tech", label: "IT/기술", icon: "💻" },
  { id: "other", label: "기타", icon: "📌" },
] as const;

export type MeetingCategory = typeof meetingCategories[number]["id"];
